import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/api/client';
import { SUNRICH_CATALOGUE, SunrichItem } from '@/data/sunrichCatalogue';

interface OrderLine {
  name: string;
  price: number;
  quantity: number;
}

/**
 * Letsie's List — the Sunrich order. Build the list from the price list,
 * see the total before sending, then save it as a purchase order so there
 * is a record of what was ordered and at what price.
 */
export default function LetsiesListPanel({ branchName }: { branchName?: string }) {
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('All');
  const [lines, setLines] = useState<Record<string, OrderLine>>({});
  const [expected, setExpected] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toISOString().split('T')[0];
  });
  const [notes, setNotes] = useState('');
  const [orders, setOrders] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const groups = useMemo(() => ['All', ...Array.from(new Set(SUNRICH_CATALOGUE.map((i) => i.group)))], []);

  const loadOrders = async () => {
    try {
      const res = await apiClient.listPurchaseOrders();
      const list = Array.isArray(res.data) ? res.data : res.data?.orders || [];
      setOrders(list.slice(0, 8));
    } catch {
      setOrders([]);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const visible = SUNRICH_CATALOGUE.filter((i) => {
    if (group !== 'All' && i.group !== group) return false;
    if (!search.trim()) return true;
    return i.name.toLowerCase().includes(search.trim().toLowerCase());
  });

  const add = (item: SunrichItem) =>
    setLines((prev) => ({
      ...prev,
      [item.name]: prev[item.name]
        ? { ...prev[item.name], quantity: prev[item.name].quantity + 1 }
        : { name: item.name, price: item.price, quantity: 1 },
    }));

  const setQty = (name: string, q: number) =>
    setLines((prev) => {
      if (q <= 0) {
        const { [name]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [name]: { ...prev[name], quantity: q } };
    });

  const orderLines = Object.values(lines);
  const total = orderLines.reduce((s, l) => s + l.price * l.quantity, 0);

  const submit = async () => {
    if (orderLines.length === 0) {
      setErr('Add something to the list first.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const supplierId = await ensureSunrichSupplier();
      await apiClient.createPurchaseOrder(
        supplierId,
        orderLines.map((l) => ({
          itemName: l.name,
          quantity: l.quantity,
          unitPrice: l.price.toFixed(2),
        })),
        expected,
        notes.trim() || `Sunrich order for ${branchName || 'the bakery'}`,
      );
      setMsg(`Order saved — ${orderLines.length} items, R ${total.toFixed(2)}.`);
      setLines({});
      setNotes('');
      await loadOrders();
    } catch (e: any) {
      const d = e?.response?.data?.message;
      setErr(Array.isArray(d) ? d.join(', ') : d || 'Could not save the order');
    } finally {
      setBusy(false);
    }
  };

  /** Sunrich is the standing supplier; create the record once if missing. */
  const ensureSunrichSupplier = async (): Promise<string> => {
    const res = await apiClient.listSuppliers();
    const list = Array.isArray(res.data) ? res.data : res.data?.suppliers || [];
    const found = list.find((s: any) => String(s.name).toLowerCase().includes('sunrich'));
    if (found) return found.id;
    const created = await apiClient.createSupplier(
      'Sunrich',
      'Sunrich sales',
      undefined,
      undefined,
      undefined,
      'COD',
    );
    return created.data.id;
  };

  return (
    <div className="space-y-6">
      {msg && <div className="bg-green-50 text-green-800 rounded-lg px-4 py-3 text-sm font-medium">{msg}</div>}
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Catalogue */}
        <div className="bg-white rounded-lg shadow flex flex-col">
          <div className="px-5 py-4 border-b">
            <h3 className="text-lg font-medium text-gray-900">Sunrich price list</h3>
            <p className="text-sm text-gray-500">{SUNRICH_CATALOGUE.length} items · tap to add</p>
          </div>
          <div className="p-4 space-y-3 border-b">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search the price list…"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
            <div className="flex gap-2 overflow-x-auto pb-1">
              {groups.map((g) => (
                <button
                  key={g}
                  onClick={() => setGroup(g)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    group === g ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-y-auto max-h-[420px] divide-y">
            {visible.map((i) => (
              <button
                key={i.name}
                onClick={() => add(i)}
                className="w-full text-left px-5 py-3 hover:bg-amber-50 flex justify-between items-center gap-3"
              >
                <span className="text-sm font-medium text-gray-900">{i.name}</span>
                <span className="text-sm font-bold text-amber-700 tabular-nums shrink-0">R {i.price}</span>
              </button>
            ))}
            {visible.length === 0 && <p className="px-5 py-10 text-center text-gray-500">Nothing matches.</p>}
          </div>
        </div>

        {/* The list */}
        <div className="bg-white rounded-lg shadow flex flex-col">
          <div className="px-5 py-4 border-b flex justify-between items-baseline">
            <h3 className="text-lg font-medium text-gray-900">The order</h3>
            <span className="text-sm text-gray-500">{orderLines.length} items</span>
          </div>

          <div className="flex-1 overflow-y-auto max-h-[300px]">
            {orderLines.length === 0 ? (
              <p className="px-5 py-12 text-center text-gray-500 text-sm">
                Pick items from the price list to build the order.
              </p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {orderLines.map((l) => (
                    <tr key={l.name} className="border-b last:border-0">
                      <td className="py-2.5 px-5">
                        <p className="font-medium text-gray-900 text-[13px] leading-tight">{l.name}</p>
                        <p className="text-xs text-gray-500 tabular-nums">R {l.price} each</p>
                      </td>
                      <td className="py-2.5 px-2 w-20">
                        <input
                          type="number"
                          min={0}
                          value={l.quantity}
                          onChange={(e) => setQty(l.name, parseInt(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded tabular-nums text-right font-semibold"
                        />
                      </td>
                      <td className="py-2.5 px-5 text-right tabular-nums font-bold w-24">
                        R {(l.price * l.quantity).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="border-t p-5 space-y-4 bg-gray-50">
            <div className="flex justify-between items-baseline">
              <span className="text-lg font-bold text-gray-900">Order total</span>
              <span className="text-2xl font-bold tabular-nums text-gray-900">R {total.toFixed(2)}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expected delivery</label>
                <input
                  type="date"
                  value={expected}
                  onChange={(e) => setExpected(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="optional"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <button
              onClick={submit}
              disabled={busy || orderLines.length === 0}
              className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg"
            >
              {busy ? 'Saving…' : 'Save order'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 px-6 py-4 border-b">Past orders</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-2.5 px-6 text-xs font-semibold uppercase tracking-wide text-gray-600">Ordered</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Expected</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Status</th>
                <th className="text-right py-2.5 px-6 text-xs font-semibold uppercase tracking-wide text-gray-600">Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o: any, i: number) => (
                <tr key={o.id || i} className="border-b last:border-0">
                  <td className="py-2.5 px-6 text-gray-700">
                    {o.orderDate ? new Date(o.orderDate).toLocaleDateString('en-ZA', { dateStyle: 'medium' }) : '—'}
                  </td>
                  <td className="py-2.5 px-4 text-gray-600">
                    {o.expectedDelivery ? new Date(o.expectedDelivery).toLocaleDateString('en-ZA', { dateStyle: 'medium' }) : '—'}
                  </td>
                  <td className="py-2.5 px-4">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 capitalize">
                      {o.status || 'draft'}
                    </span>
                  </td>
                  <td className="py-2.5 px-6 text-right tabular-nums font-semibold">
                    R {Number(o.totalAmount ?? 0).toFixed(2)}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-gray-500">
                    No orders saved yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
